import React, { useMemo, useEffect, useRef } from 'react';
import sortBy from 'lodash/sortBy';
import { filterNullish } from '@modernhealth/filter-nullish';
import { useFormContext, FormInputSelect } from '@modernhealth/vital-components';
import { useSelector } from 'react-redux';

import {
    useAllTimezonesQuery,
    AllTimezonesQuery,
} from 'src/modules/user/documents/allTimeZonesQuery.generated';
import { getUserInformation } from '../selectors/userSelectors';

interface Props {
    name: string;
    label?: string;
    labelledBy?: string;
    required?: boolean;
    autoDetectDefaultValue?: boolean;
}

type LocalTimeZoneNode = NonNullable<NonNullable<AllTimezonesQuery['allTimezones']>[number]>;

export function formatTimeZoneLabel(tz: LocalTimeZoneNode) {
    return `(${tz.utcOffsetFormatted}) ${tz.commonName} - ${tz.locality}`;
}

export const FormInputTimeZone: React.FC<Props> = ({
    name,
    label,
    labelledBy,
    autoDetectDefaultValue,
    required,
}) => {
    const { data, loading, refetch } = useAllTimezonesQuery({
        fetchPolicy: 'cache-and-network', // needed to update cache after refetch
    });
    const user = useSelector(getUserInformation);
    const { watch, setValue } = useFormContext();
    const value = watch(name) as string | undefined;

    const initialLangPreference = useRef(user.languagePreferences?.[0]);
    const langPreference = user.languagePreferences?.[0];

    const timeZoneNodes = useMemo(
        () => (data?.allTimezones ? filterNullish(data.allTimezones) : []),
        [data?.allTimezones],
    );

    const options = useMemo(
        () =>
            sortBy(timeZoneNodes, ['utcOffset', 'properName']).map(n => ({
                label: formatTimeZoneLabel(n),
                value: n.properName,
            })),
        [timeZoneNodes],
    );

    useEffect(() => {
        if (langPreference !== initialLangPreference.current) {
            initialLangPreference.current = langPreference;

            refetch();
        }
    }, [langPreference, refetch]);

    useEffect(() => {
        if (options.length === 0) {
            return;
        }

        const optionSelected = value && value.length > 0;

        if (optionSelected && !options.find(n => n.value === value)) {
            // When the currently selected value doesn't match one of
            // the options from the server...reset it.
            setValue(name, '');
        } else if (!optionSelected && autoDetectDefaultValue) {
            // If the autoDetectDefaultValue is enabled then match the
            // browser's time zone with one of the options from the
            // server and dynamically set the "default" value
            // for the member.
            const deviceTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
            const timeZone = options.find(n => n.value === deviceTimeZone);
            if (timeZone) {
                setValue(name, timeZone.value);
            }
        }
    }, [options, name, value, setValue, autoDetectDefaultValue]);

    return (
        <FormInputSelect
            data-loaded={!loading} // supports testing
            name={name}
            label={label}
            labelledBy={labelledBy}
            options={options}
            required={required}
        />
    );
};
